import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import IndexHeader from "app/shared-components/IndexHeader";
import { reportService } from "src/app/services/api/report.service";
import { downloadService } from "src/app/services/api/download.service";
import { HostRegistrationRaw } from "src/app/@types/interfaces/report.interface";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AdminHostRegistrations = () => {
  const [data, setData] = useState<HostRegistrationRaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    program: "",
    from: "",
    to: "",
  });

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (filters.program && r["Program Registered For"] !== filters.program)
        return false;

      if (filters.from && filters.from.trim() !== "") {
        const regDate = new Date(r["Registration Date"]);
        const fromDate = new Date(filters.from);
        if (regDate < fromDate) return false;
      }

      if (filters.to && filters.to.trim() !== "") {
        const regDate = new Date(r["Registration Date"]);
        const toDate = new Date(filters.to);
        if (regDate > toDate) return false;
      }

      return true;
    });
  }, [data, filters]);

  const paginatedData = useMemo(() => {
    return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const resp = await reportService.getTotalHostRegistrationsByProgram();

        if (resp.success && resp.data?.registrations) {
          const rawData = resp.data.registrations;
          setData(rawData);
        } else {
          throw new Error(resp.message || "Failed to load data");
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFilterChange =
    (field: keyof typeof filters) =>
    (
      e:
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      setFilters((f) => ({ ...f, [field]: e.target.value }));
      setPage(0);
    };

  const handleClear = () => {
    setFilters({ program: "", from: "", to: "" });
    setPage(0);
  };

  const handleGenerate = () => {
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadExcel = () => {
    const filename = "Total_Host_Registrations_by_Program.xlsx";
    const filepath = `${process.env.PUBLIC_URL}/assets/data/Total Host Registrations by Program.xlsx`;
    downloadService.downloadFileWithPath(filename, filepath);
  };

  const downloadPDF = () => {
    const filename = "Total_Host_Registrations_by_Program.pdf";
    const filepath = `${process.env.PUBLIC_URL}/assets/data/Total Host Registrations by Program.pdf`;
    downloadService.downloadFileWithPath(filename, filepath);
  };

  if (loading) {
    return (
      <>
        <IndexHeader
          name="Total Host Registrations by Program"
          update={false}
          add={false}
          redirectingPath="/reports/total-host-registrations"
        />
        <Box m={4} textAlign="center">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <IndexHeader
          name="Total Host Registrations by Program"
          update={false}
          add={false}
          redirectingPath="/reports/total-host-registrations"
        />
        <Box m={4} textAlign="center">
          <Typography color="error">{error}</Typography>
        </Box>
      </>
    );
  }

  const programs = Array.from(
    new Set(data.map((r) => r["Program Registered For"]))
  );

  return (
    <>
      <IndexHeader
        name="Total Host Registrations by Program"
        update={false}
        add={false}
        redirectingPath="/reports/total-host-registrations"
      />

      <Box m={3}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Program</InputLabel>
              <Select
                value={filters.program}
                label="Program"
                onChange={handleFilterChange("program")}
              >
                {programs.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item>
            <TextField
              label="From"
              type="date"
              size="small"
              value={filters.from}
              onChange={handleFilterChange("from")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item>
            <TextField
              label="To"
              type="date"
              size="small"
              value={filters.to}
              onChange={handleFilterChange("to")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item>
            <Button variant="outlined" onClick={handleClear}>
              Clear All
            </Button>
          </Grid>

          <Grid item>
            <Button variant="contained" onClick={handleGenerate}>
              Generate
            </Button>
          </Grid>
        </Grid>

        <Box mt={2} display="flex" gap={2}>
          <Button variant="contained" onClick={downloadPDF}>
            Download (PDF)
          </Button>
          <Button variant="contained" onClick={downloadExcel}>
            Download (Excel)
          </Button>
        </Box>
      </Box>

      <Box m={3}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Host ID</StyledTableCell>
                <StyledTableCell>Host Organization</StyledTableCell>
                <StyledTableCell>Primary Contact Name</StyledTableCell>
                <StyledTableCell>Primary Contact Email</StyledTableCell>
                <StyledTableCell>Address</StyledTableCell>
                <StyledTableCell>Organization Type</StyledTableCell>
                <StyledTableCell>Registration Date</StyledTableCell>
                <StyledTableCell>Program Registered For</StyledTableCell>
                <StyledTableCell>No. of Vacancies</StyledTableCell>
                <StyledTableCell>No. of Jobs Offered</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((r) => (
                  <TableRow key={r["Host ID"]}>
                    <StyledTableCell>{r["Host ID"]}</StyledTableCell>
                    <StyledTableCell>
                      {r["Host Organization Name"]}
                    </StyledTableCell>
                    <StyledTableCell>
                      {r["Primary Contact Name"]}
                    </StyledTableCell>
                    <StyledTableCell>
                      {r["Primary Contact Email"]}
                    </StyledTableCell>
                    <StyledTableCell>{r.Address}</StyledTableCell>
                    <StyledTableCell>{r["Organization Type"]}</StyledTableCell>
                    <StyledTableCell>{r["Registration Date"]}</StyledTableCell>
                    <StyledTableCell>
                      {r["Program Registered For"]}
                    </StyledTableCell>
                    <StyledTableCell>{r["No. of Vacancies"]}</StyledTableCell>
                    <StyledTableCell>
                      {r["No. of Jobs Offered"]}
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={10} align="center">
                    No records found.
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </>
  );
};

export default AdminHostRegistrations;
